import type { PrintRotation } from '@tsagko/label-contracts'
import { useTranslation } from 'react-i18next'

interface PrintRotationControlProps {
  value: PrintRotation | undefined
  onChange: (next: PrintRotation) => void
}

const DEFAULT: PrintRotation = { mode: 'auto', direction: 'cw' }

/** Template-level print rotation. Label is authored in reading orientation;
 *  this only affects print output. Sits next to the dimension lock. */
export function PrintRotationControl({ value, onChange }: PrintRotationControlProps) {
  const { t } = useTranslation()
  const v = value ?? DEFAULT
  const active = v.mode !== 'off'
  return (
    <span
      data-testid="print-rotation-control"
      className="ml-2 inline-flex items-center gap-0.5 text-[10px]"
    >
      <select
        aria-label={t('canvas.printRotation.mode')}
        data-testid="print-rotation-mode"
        value={v.mode}
        onChange={e => onChange({ ...v, mode: e.target.value as PrintRotation['mode'] })}
        className="rounded border border-stone-300 px-1 py-0.5 font-mono text-[10px] dark:border-stone-700 dark:bg-stone-900 dark:text-stone-100"
      >
        <option value="auto">{t('canvas.printRotation.modeAuto')}</option>
        <option value="on">{t('canvas.printRotation.modeOn')}</option>
        <option value="off">{t('canvas.printRotation.modeOff')}</option>
      </select>
      <button
        type="button"
        data-testid="print-rotation-direction"
        aria-label={t(
          v.direction === 'cw' ? 'canvas.printRotation.cw' : 'canvas.printRotation.ccw'
        )}
        disabled={v.mode === 'off'}
        title={t(
          v.direction === 'cw' ? 'canvas.printRotation.cwTitle' : 'canvas.printRotation.ccwTitle'
        )}
        onClick={() => onChange({ ...v, direction: v.direction === 'cw' ? 'ccw' : 'cw' })}
        className={[
          'flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded border text-[12px]',
          active
            ? 'border-amber-400 bg-amber-50 text-amber-600 dark:border-amber-600 dark:bg-amber-950 dark:text-amber-400'
            : 'border-stone-300 text-stone-500 dark:border-stone-700 dark:text-stone-400',
          'disabled:cursor-not-allowed disabled:opacity-50',
        ].join(' ')}
      >
        {v.direction === 'cw' ? '↻' : '↺'}
      </button>
    </span>
  )
}
